import { CommonModule, isPlatformBrowser } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, ElementRef, ViewChild, PLATFORM_ID, Inject } from "@angular/core";

@Component({
  selector: "app-skeleton-image",
  templateUrl: "./skeleton-image.component.html",
  styleUrls: ["./skeleton-image.component.scss"],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkeletonImageComponent implements OnInit, OnChanges {
  @ViewChild("imageRef", { static: false }) imageRef!: ElementRef;
  dateLimit: Date = new Date("2024-03-13 14:00:00");
  validDate: boolean = false; // Replace signal with plain boolean
  isInViewport: boolean = false; // Replace signal with plain boolean
  currentSrc: string = ""; // Replace signal with plain string

  @Input({ required: true }) src!: string;
  @Input() alt: string = "";
  @Input() lazy: boolean = true;
  @Input() customClass: string = "";
  @Input() created: string = "";
  @Input() size: number; // Explicitly type as number (assuming it's a number)
  @Input() height: number; // Explicitly type as number

  private _aspectRatio: string = "";
  @Input() set aspectRatio(value: string) {
    this.height = this.calcHeight(value);
    this._aspectRatio = value;
  }

  get aspectRatio(): string {
    return this._aspectRatio;
  }

  array_images: string[] = []; // Type as string array
  readonly sizeArray: number[] = [320, 480, 640, 800, 1250];

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  ngOnInit(): void {
    this.generateImageSrcs(this.src);
    this.updateCurrentSrc();
    const date = new Date(this.created);
    this.validDate = date > this.dateLimit; // Set directly instead of signal
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkViewport();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.src && !changes.src.firstChange) {
      this.generateImageSrcs(changes.src.currentValue);
      this.updateCurrentSrc();
    }
  }

  generateImageSrcs(src: string): void {
    this.array_images = [src];
    this.sizeArray.forEach((size) => {
      if (src) {
        if (size === 1250) {
          this.array_images.push(src);
          return;
        }
        const modifiedSrc = src.replace(/caratula(?=\.\w+$)/i, `caratula_${size}`);
        this.array_images.push(encodeURI(modifiedSrc));
      }
    });
  }

  calcHeight(aspectRatio: string): number {
    if (!aspectRatio) return 0; // Handle case where aspectRatio is not set
    return Math.round(this.size / parseFloat(aspectRatio)); // Assuming aspectRatio is a string like "1.5"
  }

  checkViewport(): void {
    const observer = new IntersectionObserver(
      ([entry]) => {
        this.isInViewport = entry.isIntersecting; // Set directly instead of signal
        if (entry.isIntersecting) {
          this.updateCurrentSrc();
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(this.imageRef.nativeElement);
  }

  updateCurrentSrc(): void {
    if (!this.array_images.length) return;
    const width = isPlatformBrowser(this.platformId) ? window.innerWidth : 1250;
    const lowestSize = this.size >= width ? width : this.size;
    const optimalSizeIndex = this.sizeArray?.findIndex((size) => size >= lowestSize);
    const selectedIndex = optimalSizeIndex === -1 ? this.sizeArray.length - 1 : optimalSizeIndex;
    this.size = this.sizeArray[selectedIndex];
    this.height = this.calcHeight(this.aspectRatio) || this.height; // Ensure height is set correctly
    this.currentSrc = this.array_images[selectedIndex + 1];
  }
}
